import { ResponseStep } from "../types";

/** Recursively flattens an object under the given prefix, producing keys of
 *  the form `prefix__key` (and `prefix__key__<index>` for array items, 1-based
 *  to mirror the BE convention). */
function flatten(
  input: { [key: string]: unknown },
  prefix: string
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key in input) {
    const value = input[key];
    const path = `${prefix}__${key}`;
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        const itemPath = `${path}__${i + 1}`;
        if (item !== null && typeof item === "object") {
          Object.assign(
            result,
            flatten(item as Record<string, unknown>, itemPath)
          );
        } else {
          result[itemPath] = item;
        }
      });
    } else if (value !== null && typeof value === "object") {
      Object.assign(result, flatten(value as Record<string, unknown>, path));
    } else {
      result[path] = value;
    }
  }
  return result;
}

/** Flatten an OnboardingApplication's responses array into a single map keyed
 *  by `slug__fieldKey`. Mirrors the backend helper in
 *  `packages/acquire-api/src/services/OnboardingApplicationService/prefixedResponses.ts`
 *  so FE-side Handlebars templates can reference prior-step fields using the
 *  same `{{{slug__fieldKey}}}` convention as EffectivSettings / DocuSign. */
export default function prefixedResponses(
  responses: Pick<ResponseStep, "slug" | "fields">[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const response of responses) {
    Object.assign(result, flatten(response.fields, response.slug));
  }
  return result;
}
